import {JsonProperty} from "../common/json/json-property";
import {JsonObject} from "../shared/json/json-object";
import {FormGen} from "../shared/formgen";
import {md5_uuid} from "../common/md5";
import {JSON_OBJECT} from "../common/json/helpers";
import {
  Appointment,
  BasePracticeListItem,
  BasePracticeListItemsLoader,
  PatientProfile,
  patientProfileAutocompleteCreateAction,
  patientProfileAutocompleteLoaderCreator,
  PatientProfiles,
  ProfileKeyType
} from "../libwyze-shared/types";
import {BaseListItem, BaseListItemsLoader, LoginCredentials} from "@/shared/types";
import {
  DEVICE_MODEL_INFO_KTM,
  DEVICE_ORIENTATION_KTS,
  DeviceModel,
  DeviceModelInfo,
  DeviceOrientation
} from "../libwyze-common/types";
import {BaseApp} from "../shared/BaseApp";
import {StyledBoxColumn} from "../shared/StyledComponents";
import {PAGE_FRAGMENT_LG_WIDTH, PD_XLG} from "../shared/dimens";
import {TextareaAutosize} from "@mui/material";

export enum CheckinErrorCodes {
  ERROR_NO_SKU_ID = 1001,
  ERROR_NO_PRACTICE_ID = 1002,
}

export type CheckinCode = {
  practiceId: string,
}

export type CheckinData = {
  register?: CheckinRegister,
  checkin?: CheckinCheckin,
}

export enum CheckinType {
  REGISTER = "register",
  CHECKIN = "checkin",
}

@JsonObject()
export class CheckinCheckin {

  @JsonProperty()
  patientId?: string;
}

@JsonObject()
export class CheckinRegister {

  @JsonProperty()
  firstname: string;

  @JsonProperty()
  lastname: string;

  @JsonProperty()
  gender: string;

  @JsonProperty()
  profilePhoto: string;

  @JsonProperty()
  dateOfBirth: number;

  @JsonProperty()
  phoneNumber: string;

  @JsonProperty()
  email: string;

  @JsonProperty()
  reason: string;
}

@JsonObject()
export class Checkin extends BasePracticeListItem {

  patient: PatientProfile;

  @FormGen({
    name: "Patient",
    type: "autocomplete",
    noneditable: true,
    required: true,
    autocompleteCreateAction: patientProfileAutocompleteCreateAction,
    autocompleteLoader: async function (value, inputText) {
      return patientProfileAutocompleteLoaderCreator(ProfileKeyType.ID)();
    },
  })
  @JsonProperty()
  patientId: string;

  nextAppt?: Appointment;
  prevAppt?: Appointment;

  @JsonProperty()
  readonly type: CheckinType;

  @JsonProperty()
  checkin?: CheckinCheckin; // When type === CheckinType.CHECKIN

  @JsonProperty()
  register?: CheckinRegister; // When type === CheckinType.REGISTER

  @JsonProperty()
  read: boolean;

  static createNewCheckin(checkin: CheckinCheckin): Checkin {
    const item = new Checkin(md5_uuid(), CheckinType.CHECKIN);
    item.checkin = checkin;
    return item;
  }

  static createNewRegister(register: CheckinRegister): Checkin {
    const item = new Checkin(md5_uuid(), CheckinType.REGISTER);
    item.register = register;
    return item;
  }

  constructor(id: string, type: CheckinType) {
    super(id);
    this.type = type;
  }
}

export class Checkins extends BasePracticeListItemsLoader<Checkin> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "checkins";
  }

  protected async onAfterItemDeserialized(item: Checkin, val?: any): Promise<Checkin> {
    const patientId = item.checkin?.patientId;
    if (item.type === CheckinType.CHECKIN && patientId) {
      item.patient = await new PatientProfiles(this.practiceId).getOrLoadItem(patientId);
      if (item.patient) {
        // const appointments = (await new Appointments(this.practiceId).loadAppointmentsByPatientProfile(item.patient));
        // item.nextAppt = findNextAppointment(appointments);
        // item.prevAppt = findPrevAppointment(appointments);
      }
    }
    return super.onAfterItemDeserialized(item, val);
  }

  protected deserializeItem(value: any): Checkin {
    return JSON_OBJECT.deserializeObject(value, Checkin);
  }

  protected serializeItem(item: Checkin): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Checkin, item2: Checkin): number {
    return item2.created - item1.created;
  }
}

export function getDeviceLoginLink(autoLogin: LoginCredentials) {
  // const config: AppletConfig = {
  //   autoLogin: autoLogin,
  // };
  // const encodedConfig = btoa(JSON.stringify(JSON_OBJECT.serializeObject(config)));
  // return getLauncherHost() + "?acfg=" + encodedConfig;
}

@JsonObject()
export class BaseDevice extends BasePracticeListItem {

  static from(device: BaseDevice) {
    return new BaseDevice(device.id);
  }

  constructor(id: string) {
    super(id);
  }
}

@JsonObject()
export class DeviceLogin {

  @JsonProperty()
  readonly email: string;

  @JsonProperty()
  readonly password: string;

  @JsonProperty()
  readonly practiceId: string;

  constructor(email: string, password: string, practiceId: string) {
    this.email = email;
    this.password = password;
    this.practiceId = practiceId;
  }
}

@JsonObject()
export class DeviceUnit extends BaseListItem {

  static createNew(setupCode: string, model?: DeviceModel): DeviceUnit {
    return new DeviceUnit(setupCode, model);
  }

  @JsonProperty()
  @FormGen({
    name: "Device model",
    type: "enum",
    enumValues: DEVICE_MODEL_INFO_KTM.kts("name").values,
    required: true,
  })
  model: DeviceModel;

  // Not serialized. Only for UI.
  @FormGen({
    name: "Override SKU ID",
    type: "string",
    maxLength: 9,
    transformText: str => str.toUpperCase(),
    description: "[OPTIONAL] Override the SKU ID for demo devices, for easier to remember IDs (e.g. DEMCHKIN1)",
  })
  overrideId: string;

  @JsonProperty()
  practiceId: string;

  @JsonProperty()
  deviceId: string;

  constructor(setupCode: string, model?: DeviceModel) {
    super(setupCode);
    this.model = model;
  }

  getModelInfo(): DeviceModelInfo {
    return DEVICE_MODEL_INFO_KTM.findMetadata(this.model);
  }
}

export class DeviceUnits extends BaseListItemsLoader<DeviceUnit> {

  private static instance;

  static getInstance(): DeviceUnits {
    if (!this.instance) {
      this.instance = new DeviceUnits();
    }
    return this.instance;
  }

  constructor() {
    super({
      shared: true,
    });
  }

  protected basePath(): string {
    return "deviceUnits";
  }

  async addListItem(item: DeviceUnit, ...rels): Promise<void> {
    if (item.overrideId) {
      item.id = item.overrideId;
    }
    return super.addListItem(item, ...rels);
  }

  protected deserializeItem(value: any): DeviceUnit {
    return JSON_OBJECT.deserializeObject(value, DeviceUnit);
  }

  protected serializeItem(item: DeviceUnit): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: DeviceUnit, item2: DeviceUnit): number {
    return item1.created - item2.created;
  }
}

@JsonObject()
export class LocationKey {

  @JsonProperty()
  id: string;

  @JsonProperty()
  name: string;

  @JsonProperty()
  country: string; // Country code.
}

@JsonObject()
export class PhotosDeviceAppConfig {

  @JsonProperty()
  @FormGen({name: "Playlist", type: "string"})
  playlistId: string;
}

@JsonObject()
export class DeviceAppConfigs {

  @JsonProperty()
  @FormGen({name: "Photos", type: "object"})
  photos: PhotosDeviceAppConfig = new PhotosDeviceAppConfig();
}

@JsonObject()
export class DeviceConfig {

  @JsonProperty()
  @FormGen({name: "App configs", type: "object"})
  readonly appConfigs: DeviceAppConfigs = new DeviceAppConfigs();
}

// These are devices that are in "provisioned" state (ie. setup is completed).
@JsonObject()
export class Device extends BaseDevice {

  static createNew(deviceUnitId: string, title: string): Device {
    const device = new Device(md5_uuid(), deviceUnitId);
    device.title = title;
    return device;
  }


  @JsonProperty()
  readonly deviceUnitId: string;

  @FormGen({name: "Title", type: "string", required: true})
  @JsonProperty()
  title: string;

  @JsonProperty()
  @FormGen({
    name: "Orientation",
    type: "enum",
    required: true,
    enumValues: DEVICE_ORIENTATION_KTS.values,
    enumDefaultKey: DeviceOrientation.LANDSCAPE
  })
  orientation: DeviceOrientation = DeviceOrientation.LANDSCAPE;

  @FormGen({
    name: "View Info",
    type: "button",
    buttonText: "Click to View",
    buttonOnClick: (target) => {
      BaseApp.CONTEXT.showCustomDialog(
        "Info",
        <StyledBoxColumn style={{padding: PD_XLG}}>
          <TextareaAutosize minRows={24} maxRows={24} disabled={true}
                            value={JSON.stringify(JSON_OBJECT.serializeObject(target), null, 2)}/>
        </StyledBoxColumn>,
        null,
        null,
        null,
        {width: "84dvw", maxWidth: PAGE_FRAGMENT_LG_WIDTH});
    }
  })
  viewInfoButton: void;

  @JsonProperty()
  config: DeviceConfig;

  constructor(id: string, deviceUnitId: string) {
    super(id);
    this.deviceUnitId = deviceUnitId;
  }
}

export class Devices extends BasePracticeListItemsLoader<Device> {

  constructor(practiceId: string) {
    super(practiceId);
  }

  protected practiceDataBasePath(): string {
    return "devices";
  }

  async addDeviceConfig(device: Device, config: DeviceConfig): Promise<void> {
    return super.addChildItem(device, "config", config);
  }

  protected deserializeItem(value: any): Device {
    return JSON_OBJECT.deserializeObject(value, Device);
  }

  protected serializeItem(item: Device): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Device, item2: Device): number {
    return item1.created - item2.created;
  }
}
